import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import UseAxios from '../../Api/http/http';
import { getSkillsSampleFile, uploadSkills } from '../../Api/request/skills';
import '../Finance/EmployeeCostDetails/EmployeeCostDetails.scss';

const ACCEPTED_EXTENSIONS = ['xlsx', 'xls', 'csv'];

const getResponsePayload = (response) => {
  if (response?.data?.isAxiosError) return response.data.response?.data || response.data;
  return response?.data || {};
};

const validateFile = (file) => {
  if (!file) return 'Please select a file.';
  const nameParts = file.name.split('.');
  const ext = nameParts.length ? nameParts[nameParts.length - 1].toLowerCase() : '';
  if (!ACCEPTED_EXTENSIONS.includes(ext)) {
    return `Invalid file type. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}.`;
  }
  return '';
};

const ErrorReport = ({ serverReport }) => {
  if (!serverReport) return null;
  const failedRows = serverReport.failedRows || serverReport.validationErrors || [];
  if (!failedRows.length && !serverReport.message) return null;

  return (
    <div className="employee-cost-error-report-container">
      <div className="employee-cost-error-header">Upload Issues:</div>
      {serverReport.message ? (
        <div className="employee-cost-error-summary">
          {serverReport.message}
        </div>
      ) : null}
      <ul className="employee-cost-error-list">
        {failedRows.map((item, idx) => {
          const detail = item.reason || item.error || 'Invalid format';
          const rowNum = item.rowNumber || item.row;
          const label = rowNum ? `Row ${rowNum} : ${detail}` : detail;
          return <li key={idx}>{label}</li>;
        })}
      </ul>
    </div>
  );
};

ErrorReport.propTypes = {
  serverReport: PropTypes.shape({
    message: PropTypes.string,
    failedRows: PropTypes.arrayOf(PropTypes.shape({
      row: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      rowNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      reason: PropTypes.string,
      error: PropTypes.string,
    })),
    validationErrors: PropTypes.arrayOf(PropTypes.shape({
      row: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      rowNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      reason: PropTypes.string,
      error: PropTypes.string,
    })),
  }),
};

ErrorReport.defaultProps = {
  serverReport: null,
};

const SkillUploadDialog = ({ open, onClose, onUpload, onSuccess }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [serverReport, setServerReport] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const reset = () => {
    setFile(null);
    setFileError('');
    setServerReport(null);
  };

  const handleClose = () => {
    if (!isUploading) {
      reset();
      onClose();
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    const error = validateFile(selectedFile);
    setFileError(error);
    setFile(error ? null : selectedFile);
    setServerReport(null);
    event.target.value = '';
  };

  const handleUpload = async () => {
    const error = validateFile(file);
    if (error) {
      setFileError(error);
      return;
    }

    if (onUpload) {
      const selectedFile = file;
      reset();
      onClose();
      onUpload(selectedFile);
      return;
    }

    setIsUploading(true);
    setServerReport(null);
    try {
      const response = await UseAxios(uploadSkills(file));
      const payload = getResponsePayload(response);
      const failedRows = payload.failedRows || payload.summary?.failedRows;

      if (response?.data?.isAxiosError || response?.status >= 400 || (failedRows && failedRows.length > 0)) {
        setServerReport({ ...payload, failedRows });
        toast.error(payload.message || 'Upload completed with errors.');
      } else {
        toast.success(payload.message || 'Skills uploaded successfully.');
        reset();
        onClose();
        onSuccess?.();
      }
    } catch (err) {
      toast.error('Upload failed. Please check your connection.');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadSample = async () => {
    try {
      const response = await UseAxios(getSkillsSampleFile());
      const payload = getResponsePayload(response);
      const signedUrl = payload?.data?.signedUrl || payload?.signedUrl;
      if (!signedUrl) {
        toast.error(payload?.message || 'Failed to download sample file.');
        return;
      }

      // The signed URL already carries response-content-disposition/type, so a
      // direct navigation lets GCS force the download with the correct filename.
      const link = document.createElement('a');
      link.href = signedUrl;
      link.style.display = 'none';
      document.body.append(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    } catch (err) {
      toast.error(err?.message || 'Failed to download sample file.');
    }
  };

  const dialogHeader = (
    <React.Fragment>
      <span>Upload Skills</span>
      <button
        type="button"
        onClick={downloadSample}
        className="employee-cost-upload-sample-link"
      >
        Sample File
      </button>
    </React.Fragment>
  );

  const dialogFooter = (
    <div className="employee-cost-dialog-footer">
      <Button
        label="Cancel"
        className="p-button p-button-text"
        onClick={handleClose}
        disabled={isUploading}
      />
      <Button
        label="Upload"
        className="p-button p-button-add"
        onClick={handleUpload}
        disabled={isUploading || !file}
      />
    </div>
  );

  return (
    <Dialog
      visible={open}
      onHide={handleClose}
      header={dialogHeader}
      footer={dialogFooter}
      className="employee-cost-dialog"
      style={{ width: '480px' }}
      modal
      draggable={false}
      resizable={false}
      closable={false}
    >
      <div className="employee-cost-upload-body">
        <div className="employee-cost-upload-field-label">
          Select file to upload
        </div>

        <div className="employee-cost-upload-file-row">
          <Button
            type="button"
            label="Choose file"
            className="p-button employee-cost-upload-choose-btn"
            onClick={() => inputRef.current?.click()}
            disabled={isUploading}
          />
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <span
            className="employee-cost-upload-filename"
            title={file ? file.name : ''}
          >
            {file ? file.name : 'No file chosen'}
          </span>
        </div>

        {fileError && (
          <div className="employee-cost-upload-error">{fileError}</div>
        )}

        <div className="employee-cost-upload-hint">
          Accepted formats: {ACCEPTED_EXTENSIONS.join(', ')}
        </div>

        <ErrorReport serverReport={serverReport} />
      </div>
    </Dialog>
  );
};

SkillUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func,
  onSuccess: PropTypes.func,
};

SkillUploadDialog.defaultProps = {
  onUpload: null,
  onSuccess: null,
};

export default SkillUploadDialog;
